import React from "react"

import { startCase } from "../../utils/change-case"
import { classNames } from "../../utils/class-names"
import OptimizedImage from "../optimized-image"
import * as styles from "./icon-static-map.module.scss"
import {
  BackgroundColor,
  Color,
  ICON_SIZE,
  IconComponent,
  IconProps,
  IconSize,
} from "./types"

const REGEX_CAPTURE_DATO_FILE_NAME = /\d+-(\w+)(-\w+)?\.\w+/

type UrlStructure = `${number}-${string}.png`

const baseIcon = (
  className: BackgroundColor | Color,
  url: UrlStructure,
  size: IconSize,
  alt: string,
) => {
  const component = () => {
    const sizeNumber = ICON_SIZE[size]
    return (
      <OptimizedImage
        loading="lazy"
        role="presentation"
        alt={alt}
        className={styles[className]}
        src={`https://www.datocms-assets.com/85985/${url}`}
        width={sizeNumber}
        height={sizeNumber}
        preset="graphic"
      />
    )
  }
  return component
}

type TestIconKeys<T extends Record<keyof T, IconComponent>> = true
type ExportsFromThisFile = typeof import("./icon-static-map")

// The following checks that all exported values (type exports not included) in this file are of type IconComponent
// If any of the exports are not of type IconComponent, the line will throw a type error
// eslint-disable-next-line no-restricted-syntax
export type _TestExportTypes = TestIconKeys<ExportsFromThisFile>

/**
 * This function generates a function component, based on the icon image urls.
 * @param mainImagePath URL of the colorless icon image
 * @param highlightImagePath URL of the colored highlight image (The frame should have the same size as the main image)
 * @param hideBaseWhenHighlighted For exceptional cases, where the base icon should be hidden when the highlight is shown
 * @returns An Icon component, with display name based on the icon name from the URL
 * The component accepts the following props:
 * - size: Icon size, either "small" or "medium"
 * - color: Icon color, either "blue", "orange", "gold", "magenta", "none" or "original"
 * - bgToContrastWith: Background color to contrast with, either "light" or "dark". This determines the color of the icon frame.
 */
const generateIconVariants = (
  mainImagePath: UrlStructure,
  highlightImagePath: UrlStructure,
  hideBaseWhenHighlighted = false,
): IconComponent => {
  // Extracts the name and alt from the URL
  const name = mainImagePath.replace(REGEX_CAPTURE_DATO_FILE_NAME, "$1")
  const alt = `${startCase(name)} Icon`
  const Icon = ({
    size = "medium",
    color,
    bgToContrastWith = "light",
    highlightAlways = false,
  }: IconProps) => {
    const IconBase = baseIcon(bgToContrastWith, mainImagePath, size, alt)
    const IconHover = baseIcon(color, highlightImagePath, size, `${alt} Hover`)
    return (
      <div className={styles.root}>
        <div
          className={classNames(
            styles.iconMain,
            hideBaseWhenHighlighted && "icon-base-hidden",
          )}
        >
          <IconBase />
        </div>
        <div
          className="icon-highlight"
          style={highlightAlways ? { opacity: 1 } : {}}
        >
          <IconHover />
        </div>
      </div>
    )
  }
  Icon.displayName = `${name}Icon`
  return Icon
}

export const AmazonCardIcon = generateIconVariants(
  "1697639020-amazon-card.png",
  "1697639016-amazon-card-hover.png",
)

export const Api = generateIconVariants(
  "1697639033-api.png",
  "1697639028-api-hover.png",
)
export const Automate = generateIconVariants(
  "1697639041-automate.png",
  "1697639037-automate-hover.png",
)
export const Bank = generateIconVariants(
  "1697639059-bank.png",
  "1697639055-bank-hover.png",
)
export const BarGraph = generateIconVariants(
  "1697639075-bar-graph.png",
  "1697639072-bar-graph-hover.png",
)
export const BriefCase = generateIconVariants(
  "1697639087-brief-case.png",
  "1697639083-brief-case-hover.png",
)
export const Calendar = generateIconVariants(
  "1697639091-calendar-dark-bg.png",
  "1697639096-calendar-hover.png",
)
export const Card = generateIconVariants(
  "1697639108-card.png",
  "1697639104-card-hover.png",
)
export const Cards = generateIconVariants(
  "1697639123-cards.png",
  "1697639120-cards-hover.png",
)
export const Charity = generateIconVariants(
  "1697639136-charity.png",
  "1697639131-charity-hover.png",
)
export const Check = generateIconVariants(
  "1697639148-check.png",
  "1697639144-check-hover.png",
)
export const Coins = generateIconVariants(
  "1697639164-coins.png",
  "1697639159-coins-hover.png",
)
export const Conversion = generateIconVariants(
  "1697639180-conversion.png",
  "1697639176-conversion-hover.png",
)
export const CreditCard = generateIconVariants(
  "1697639194-credit-card.png",
  "1697639190-credit-card-hover.png",
)
export const Customize = generateIconVariants(
  "1697639206-customize.png",
  "1697639203-customize-hover.png",
)
export const Desktop = generateIconVariants(
  "1697639217-desktop.png",
  "1697639213-desktop-hover.png",
)
export const Diary = generateIconVariants(
  "1697639220-diary-dark-bg.png",
  "1697639224-diary-hover.png",
)
export const Flag = generateIconVariants(
  "1697639234-flag.png",
  "1697639231-flag-hover.png",
)
export const Gears = generateIconVariants(
  "1697639249-gears.png",
  "1697639245-gears-hover.png",
)
export const GiftBox = generateIconVariants(
  "1699520927-gift-box.png",
  "1699520937-gift-box-hover.png",
)
export const GiftCard = generateIconVariants(
  "1697639267-gift-card.png",
  "1697639264-gift-card-hover.png",
)
export const Global = generateIconVariants(
  "1697639275-global.png",
  "1697639272-global-hover.png",
)
export const Group = generateIconVariants(
  "1697639279-group-dark-bg.png",
  "1697639284-group-hover.png",
)
export const Fraud2 = generateIconVariants(
  "1713603351-fraud2.png",
  "1713603347-fraud2-hover.png",
)
export const Help = generateIconVariants(
  "1697639317-help.png",
  "1697639291-help-hover.png",
)
export const HelpWithPerson = generateIconVariants(
  "1698780883-help-with-person-v2.png",
  "1698780891-help-with-person-hover-v2.png",
)
export const Hr = generateIconVariants(
  "1697639328-hr.png",
  "1697639324-hr-hover.png",
)
export const Integrations = generateIconVariants(
  "1697639367-integrations.png",
  "1697639358-integrations-hover.png",
)
export const Interview = generateIconVariants(
  "1697639371-interview-dark-bg.png",
  "1697639375-interview-hover.png",
)
export const List = generateIconVariants(
  "1697639386-list.png",
  "1697639383-list-hover.png",
)
export const Mail = generateIconVariants(
  "1697639397-mail.png",
  "1697639394-mail-hover.png",
)
export const Marketing = generateIconVariants(
  "1697639410-marketing.png",
  "1697639406-marketing-hover.png",
)
export const Money = generateIconVariants(
  "1697639427-money.png",
  "1697639422-money-hover.png",
)
export const Newspaper = generateIconVariants(
  "1697639440-newspaper.png",
  "1697639435-newspaper-hover.png",
)
export const OpenBook = generateIconVariants(
  "1697639453-open-book.png",
  "1697639450-open-book-hover.png",
)
export const PaintPallette = generateIconVariants(
  "1697639723-paint-pallette.png",
  "1697639718-paint-pallette-hover.png",
)
export const PaperPlane = generateIconVariants(
  "1723150986-paperplane.png",
  "1723150963-paperplane-icon-hover.png",
)
export const Person = generateIconVariants(
  "1697639734-person.png",
  "1697639730-person-hover.png",
)
export const PiggyBank = generateIconVariants(
  "1697639748-piggy-bank.png",
  "1697639744-piggy-bank-hover.png",
)
export const Rebates = generateIconVariants(
  "1697639751-rebates-dark-bg.png",
  "1697639755-rebates-hover.png",
)
export const Referral = generateIconVariants(
  "1697639897-referral-dark-bg.png",
  "1697639902-referral-hover.png",
)
export const SearchDoc = generateIconVariants(
  "1738699918-search-doc.png",
  "1738699967-search-doc-hover.png",
)
export const Security = generateIconVariants(
  "1697639935-security.png",
  "1697639931-security-hover.png",
)
export const Spreadsheet = generateIconVariants(
  "1697639946-spreadsheet.png",
  "1697639942-spreadsheet-hover.png",
)
export const Support = generateIconVariants(
  "1697639968-support.png",
  "1697639963-support-hover.png",
)
export const Survey = generateIconVariants(
  "1697639971-survey-dark-bg.png",
  "1697639974-survey-hover.png",
)
export const SurveysPlatform = generateIconVariants(
  "1698243498-surveys.png",
  "1698243730-surveys-hover.png",
)
export const Sync = generateIconVariants(
  "1698796677-sync.png",
  "1698796672-sync-hover.png",
)
export const Tremendous = generateIconVariants(
  "1712941472-tremendous.png",
  "1697639986-tremendous-hover.png",
  true, // Tremendous icon is a bit exceptional, because the edges of the T are not visible in the hover state, so the base icon must be hidden
)
export const Video = generateIconVariants(
  "1697640001-video.png",
  "1697639997-video-hover.png",
)
export const UxResearch = generateIconVariants(
  "1697546920-ux-research-96.png",
  "1697546923-ux-research-hover-96.png",
)
export const Research = generateIconVariants(
  "1697496719-research-96.png",
  "1697546916-research-hover-96.png",
)
export const Megaphone = generateIconVariants(
  "1697546892-megaphone-96.png",
  "1697546896-megaphone-hover-96.png",
)
export const MarketResearch = generateIconVariants(
  "1697546884-market-research-96.png",
  "1697546888-market-research-hover-96.png",
)
export const Employee = generateIconVariants(
  "1697546877-employee-96.png",
  "1697546881-employee-hover-96.png",
)
export const Recipient = generateIconVariants(
  "1703876586-recipient.png",
  "1703876580-recipient-hover.png",
)
export const Send = generateIconVariants(
  "1703876594-send.png",
  "1703876590-send-hover.png",
)
export const Shuffle = generateIconVariants(
  "1698310098-shuffle.png",
  "1698310092-shuffle-hover.png",
)
export const Visa = generateIconVariants(
  "1707748189-visa.png",
  "1707748184-visa-hover.png",
)
export const PhysicalCards = generateIconVariants(
  "1707748694-physical-cards.png",
  "1707748688-physical-cards-hover.png",
)
export const ProductOverview = generateIconVariants(
  "1710872321-product-overview.png",
  "1710872316-product-overview-hover.png",
)
export const Warning = generateIconVariants(
  "1711443372-warning.png",
  "1711443367-warning-hover.png",
)
export const Legal = generateIconVariants(
  "1712844568-legal.png",
  "1712844563-legal-hover.png",
)
export const CharityPlus = generateIconVariants(
  "1712847810-charity-plus.png",
  "1712847815-charity-plus-hover.png",
)

export const Code = generateIconVariants(
  "1724417588-code.png",
  "1724417601-code-hover.png",
)
